/**
 * useHeadshotUrl — resolves a viewable, presigned URL for the most recent
 * HEADSHOT document in an already-fetched document list, for surfaces that
 * want a contestant's official pageant headshot to take display priority over
 * their separately-uploaded, evergreen profile picture.
 *
 * Deliberately takes a document LIST rather than fetching one itself: callers
 * (the director review surface, the Community record page, the contestant's
 * own application view) already fetch their applicant's documents for other
 * reasons (the Documents table, the record's related-documents list, the
 * upload-status UI) — this hook adds no new document read, only the
 * presign-and-cache step for whichever one turns out to be the headshot. The
 * `pickMostRecentHeadshot` choice itself lives in the pure
 * `utils/headshotResolution.ts` so it stays independently testable.
 *
 * Presigns via `getProfilePhotoDownloadUrl(s3Key)` — the same working
 * presigner `LegacyAvatar` already uses for profile pictures (its Lambda
 * explicitly allows `applicants/*` keys, which is where a HEADSHOT document
 * lives, not just `profile-photos/*`). NOT `getDocumentDownloadUrl`: that
 * field is an unfinished stub (`graphql/resolvers/document/
 * getDocumentDownloadUrl.res.vtl`, its own comments say so) that has always
 * returned a non-functional `https://placeholder-url.s3.amazonaws.com/...`
 * URL — the headshot-priority feature never actually displayed a headshot
 * for any contestant who lacked a separate, working profile picture to fall
 * back to.
 *
 * Returns `null` while unresolved (no headshot, still loading, or the presign
 * failed) — callers pass this straight through as `LegacyAvatar`'s `directUrl`,
 * which already falls back to `profilePhotoS3Key` then initials on its own.
 *
 * `documents === undefined` means "the caller doesn't have its document list
 * loaded THIS render" (e.g. `ApplicationReviewView` passes `undefined` while
 * its own `useReviewDocuments` read is mid-refetch) — it is NOT the same
 * claim as "this applicant has no headshot." Collapsing the two used to
 * flash an already-resolved photo back to the initials fallback on every
 * upstream refetch, even when the headshot itself hadn't changed: an
 * already-displayed photo is left alone while `documents` is `undefined`,
 * and only an actual (possibly empty) array is treated as authoritative.
 */

import { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';

import { getProfilePhotoDownloadUrl } from '@/graphql/queries';
import { pickMostRecentHeadshot, type HeadshotCandidate } from '@/utils/headshotResolution';

const client = generateClient();

// Module-level cache, same precedent as ApplicantList.tsx's headshotUrlCache —
// a presigned URL is safe to reuse across renders/components for the same
// s3Key within the page's lifetime.
const urlCache = new Map<string, string>();

export function useHeadshotUrl(
  documents: readonly HeadshotCandidate[] | undefined,
): string | null {
  // `undefined` here means "no information this render" — distinct from a
  // loaded, empty-of-headshots array, which genuinely means no headshot.
  // A picked headshot with no s3Key (shouldn't happen — the GraphQL field is
  // non-null — but the TS shape stays optional defensively) is treated the
  // same as "no headshot": nothing presignable.
  const headshotKey = documents ? (pickMostRecentHeadshot(documents)?.s3Key ?? null) : undefined;
  const [url, setUrl] = useState<string | null>(() =>
    headshotKey ? urlCache.get(headshotKey) ?? null : null,
  );

  useEffect(() => {
    if (headshotKey === undefined) {
      // The caller doesn't have its documents loaded this render — leave
      // whatever is already resolved in place rather than clearing it.
      return;
    }
    if (!headshotKey) {
      setUrl(null);
      return;
    }
    const cached = urlCache.get(headshotKey);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = (await client.graphql({
          query: getProfilePhotoDownloadUrl,
          variables: { s3Key: headshotKey },
          authMode: 'userPool',
        })) as GraphQLResult<{ getProfilePhotoDownloadUrl: string }>;
        const resolved = res.data?.getProfilePhotoDownloadUrl;
        if (resolved) urlCache.set(headshotKey, resolved);
        if (!cancelled) setUrl(resolved ?? null);
      } catch (err) {
        console.warn(`Failed to fetch headshot download URL for key ${headshotKey}:`, err);
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [headshotKey]);

  return url;
}
